import './underwriting-queue-grid.scss';

import {
  AliasType,
  GetUserQuery,
  SubmissionRenewalType,
  TaskLabel,
  UserGroup,
  getDisplayNameFromEntity,
  getFirstPaginatedNode,
  getPaginatedNodes,
  useGetUsers,
  useUpsertSubmissionUserMutation,
} from '@converge/graphql';
import { Box, Stack } from '@mui/material';
import { ConvergeNavigationUrls, useConvergeSnackBar } from '../../contexts';
import { FormProvider, useForm } from 'react-hook-form';
import {
  SubmissionFieldIdType,
  SubmissionFieldLabelType,
  SubmissionStatus,
  UNDERWRITING_SORT_DEFAULTS,
} from '@converge/data';
import {
  asCase,
  capitalizeFirstLetter,
  displayFormatDate,
  displayFormatDateUTC,
  getTaskLabel,
} from '@converge/utils';
import { useCallback, useMemo } from 'react';

import { Link } from 'react-router-dom';
import { NewTypeAhead } from '../../presentation';
import QueueGrid from './queue-grid';
import { SubmissionStatusBadge } from './submission-status-badge';
import { isApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

export const getUserOptions = (userList?: GetUserQuery, getEmail?: boolean) => {
  return (
    userList?.users?.edges
      ?.map((user) => ({
        value: getEmail ? user.node?.userId : user.node.uuid,

        label: `${user.node.firstName} ${user.node.lastName}`,
      }))
      ?.sort((a, b) => a?.label.localeCompare(b?.label)) || []
  );
};
export const RENEWAL_TYPES_OPTIONS = [
  { label: 'Auto renewal', value: SubmissionRenewalType.AutoRenewal },
  { label: 'Auto quote', value: SubmissionRenewalType.AutoQuoteRenewal },
  {
    label: 'Conditional renewal',
    value: SubmissionRenewalType.ConditionalRenewal,
  },
  {
    label: 'Non renewal',
    value: SubmissionRenewalType.NonRenewal,
  },
  {
    label: 'Traditional renewal',
    value: SubmissionRenewalType.TraditionalRenewal,
  },
];

const renewalTypes = {
  [SubmissionRenewalType.AutoQuoteRenewal]: 'Auto Quote Renewal',
  [SubmissionRenewalType.AutoRenewal]: 'Auto Renewal',
  [SubmissionRenewalType.ConditionalRenewal]: 'Conditional Renewal',
  [SubmissionRenewalType.NonRenewal]: 'Non Renewal',
  [SubmissionRenewalType.TraditionalRenewal]: 'Traditional Renewal',
};
interface IUnderwritingQueueGridProps {
  headerTitle: string;
}

export const UnderwritingQueueGrid = ({
  headerTitle,
}: IUnderwritingQueueGridProps) => {
  const { t } = useTranslation();
  const { notify } = useConvergeSnackBar();

  const { upsertSubmissionUser } = useUpsertSubmissionUserMutation();
  const updateSubmissionUser = useCallback(
    async (
      submissionUuid: string,
      userUuid: string | null,
      userGroup: UserGroup
    ) => {
      const userType =
        userGroup === UserGroup.Underwriter ? 'underwriter' : 'assistant';
      try {
        notify({
          message: `Updating ${userType}...`,
          variant: 'info',
        });
        await upsertSubmissionUser({
          variables: {
            submissionUuid,
            userUuid: userUuid,
            userGroup,
          },
        });
        notify({
          message: `${capitalizeFirstLetter(userType)} saved successfully`,
          variant: 'success',
        });
      } catch (cause) {
        notify({
          message: `Failed to update ${userType}: ${
            cause instanceof Error && isApolloError(cause)
              ? cause.message
              : cause
          }`,
          variant: 'error',
        });
      }
    },
    [notify, upsertSubmissionUser]
  );

  const { data: underwriterList } = useGetUsers({
    userGroups: [UserGroup.Underwriter],
  });
  const { data: underwriterAssistantList } = useGetUsers({
    userGroups: [UserGroup.UnderwriterAssistant],
  });

  const UNDERWRITER_OPTIONS = useMemo(
    () => getUserOptions(underwriterList) ?? [],
    [underwriterList]
  );
  const UNDERWRITER_ASSISTANT_OPTIONS = useMemo(
    () => getUserOptions(underwriterAssistantList) ?? [],
    [underwriterAssistantList]
  );

  const formMethods = useForm({});

  return (
    <Box className="underwriting-queue">
      <FormProvider {...formMethods}>
        <QueueGrid
          title={headerTitle}
          columns={[
            {
              id: SubmissionFieldIdType.SubmissionReferenceId,
              label: t(SubmissionFieldLabelType.SubmissionReferenceId),
              width: '190px',

              render: (row) => (
                <>
                  {row.status === SubmissionStatus.Submitted ? (
                    <span className="disabled-reference-id">
                      {row.referenceId}
                    </span>
                  ) : (
                    <Link
                      className="reference-id"
                      to={ConvergeNavigationUrls.underwritingSubmissionDetail({
                        submissionUuid: row.uuid,
                        applicationUuid: getFirstPaginatedNode(row.applications)
                          ?.uuid,
                        submissionApplicationUuid: getFirstPaginatedNode(
                          row.applications
                        )?.submissionApplication?.node.uuid,
                      })}
                    >
                      {row.referenceId}
                    </Link>
                  )}
                </>
              ),
              isSortable: true,
            },
            {
              id: SubmissionFieldIdType.Status,
              label: t(SubmissionFieldLabelType.Status),
              headerAlign: 'center',
              render: (row) => (
                <Stack className="status-badge" alignItems="center">
                  <SubmissionStatusBadge
                    status={row.status}
                    uuid={row.uuid}
                    createdAt={row.createdAt}
                    subStatus={row.subStatus}
                  />
                </Stack>
              ),
              isSortable: true,
            },
            {
              id: SubmissionFieldIdType.InsuredName,
              label: t(SubmissionFieldLabelType.InsuredName),
              render: (row) =>
                row.insuredEntity
                  ? getDisplayNameFromEntity(row.insuredEntity.node) ?? 'N/A'
                  : 'N/A',
              isSortable: true,
            },
            {
              id: SubmissionFieldIdType.PolicyType,
              label: t(SubmissionFieldLabelType.PolicyType),
              render: (row) =>
                row.type ? asCase(row.type, asCase.StringCase.Sentence) : 'N/A',
              isSortable: true,
            },
            {
              id: SubmissionFieldIdType.RenewalStatus,
              label: t(SubmissionFieldLabelType.RenewalStatus),
              render: (row) =>
                row.renewal && row.renewalType
                  ? renewalTypes[row.renewalType]
                  : 'New business',
              isSortable: false,
            },

            {
              id: SubmissionFieldIdType.ActionItem,
              label: t(SubmissionFieldLabelType.ActionItem),
              render: (row) => {
                return getTaskLabel(
                  row?.tasks?.edges[0]?.node?.label as TaskLabel
                );
              },
              isSortable: false,
            },
            {
              id: SubmissionFieldIdType.DigitalSource,
              label: t(SubmissionFieldLabelType.DigitalSource),
              width: '180px',
              render: (row) => row?.tenantLabel ?? '',
              isSortable: true,
            },
            {
              id: SubmissionFieldIdType.Brokerage,
              label: t(SubmissionFieldLabelType.Brokerage),
              // TODO: Remove the fallback after API ticket CON-1589 is done (https://convergeins.atlassian.net/browse/CON-1589)
              render: (row) =>
                getPaginatedNodes(row.brokerEntity?.node.aliases).find(
                  (alias) => alias.primary && alias.type === AliasType.LegalName
                )?.name ?? 'N/A',
              isSortable: true,
            },
            {
              id: SubmissionFieldIdType.Broker,
              label: t(SubmissionFieldLabelType.BrokerContact),
              render: (row) => {
                const person = row.persons.edges.find(
                  (edge) => edge.node.type === 'agency_contact'
                );
                if (!person) {
                  return 'N/A';
                }
                return (
                  [person.node.firstName, person.node.lastName]
                    .filter(Boolean)
                    .join(' ') || 'N/A'
                );
              },
              isSortable: true,
            },
            {
              id: SubmissionFieldIdType.ReceivedAt,
              label: t(SubmissionFieldLabelType.ReceivedAt),
              render: (row) => displayFormatDate(row.receivedAt ?? '') || 'N/A',
              isSortable: true,
            },
            {
              id: SubmissionFieldIdType.PolicyStartDate,
              label: t(SubmissionFieldLabelType.EffectiveDate),
              render: (row) =>
                displayFormatDateUTC(row.policyStartDate ?? '') || 'N/A',
              isSortable: true,
            },
            // {
            //   id: SubmissionFieldIdType.ClearedAt,
            //   label: t(SubmissionFieldLabelType.ClearedAt),
            //   // TODO: Remove the fallback after API ticket CON-1589 is done (https://convergeins.atlassian.net/browse/CON-1589)
            //   render: (row) => 'N/A',
            //   isSortable: true,
            //   visible:
            //     config.featureFlags.showClearedAtColumnOnUnderwritingQueue ===
            //     true,
            // },
            {
              id: SubmissionFieldIdType.DueDate,
              label: t(SubmissionFieldLabelType.DueDate),
              // TODO: Remove the fallback after API ticket CON-1589 is done (https://convergeins.atlassian.net/browse/CON-1589)
              render: (row) =>
                displayFormatDateUTC(row?.dueDate ?? '') || 'N/A',
              isSortable: true,
            },
            {
              id: SubmissionFieldIdType.Assistant,
              label: t(SubmissionFieldLabelType.Assistant),
              align: 'right',
              isSortable: false,
              render: (row) => {
                const currentUnderwriterAssistant = row?.users?.edges?.filter(
                  (submission) => {
                    return (
                      submission?.node?.type === UserGroup.UnderwriterAssistant
                    );
                  }
                );
                return (
                  <div className="uw-dropdown">
                    <NewTypeAhead
                      placeholder="Select UA"
                      name=""
                      options={UNDERWRITER_ASSISTANT_OPTIONS}
                      value={currentUnderwriterAssistant[0]?.node?.uuid}
                      onChangeOption={(event) => {
                        const userUuid =
                          event?.value === '' || !event?.value
                            ? null
                            : event.value;

                        updateSubmissionUser(
                          row.uuid,
                          userUuid,
                          UserGroup.UnderwriterAssistant
                        );
                      }}
                    />
                  </div>
                );
              },
            },
            {
              id: SubmissionFieldIdType.UnderWriter,
              label: t(SubmissionFieldLabelType.UnderWriter),
              align: 'right',
              isSortable: true,
              render: (row) => {
                const currentUnderwriter = row?.users?.edges?.filter(
                  (submission) => {
                    return submission?.node?.type === UserGroup.Underwriter;
                  }
                );
                return (
                  <div className="uw-dropdown">
                    <NewTypeAhead
                      placeholder="Select UW"
                      name=""
                      options={UNDERWRITER_OPTIONS}
                      value={currentUnderwriter[0]?.node?.uuid}
                      onChangeOption={(event) => {
                        const userUuid =
                          event?.value === '' || !event?.value
                            ? null
                            : event.value;

                        updateSubmissionUser(
                          row?.uuid,
                          userUuid,
                          UserGroup.Underwriter
                        );
                      }}
                    />
                  </div>
                );
              },
            },
          ]}
          defaultSort={UNDERWRITING_SORT_DEFAULTS}
        />
      </FormProvider>
    </Box>
  );
};
